// @file Surface language panel store
import { captureFetchException } from '@@/bits/error_tracker'
import { p__, __ } from '@@/bits/intl'
import { availableLocales } from '@@/bits/locale'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { WallLanguageDetectionApi } from '@@/surface/api/wall_language_detection'
import { WallTranslationApi } from '@@/surface/api/wall_translation'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSurfaceLanguagePanelStore = defineStore('surfaceLanguagePanelStore', () => {
  const surfaceStore = useSurfaceStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceCommentsStore = useCommentsStore()

  const xSurfaceLanguagePanel = ref<boolean>(false)
  const currentLanguage = ref<string>('original')
  const unsavedLanguage = ref<string>(currentLanguage.value)
  const detectedLanguage = ref<string>('')
  const isTranslatingPadlet = ref<boolean>(false)

  // Presentable name for current language with custom logic for original language
  const currentLanguageDisplayName = computed<string>(() => {
    if (currentLanguage.value === 'original') {
      const localeName =
        availableLocales.find((locale) => locale.standard === detectedLanguage.value)?.name ?? __('Unknown')
      return p__(
        'This text appears in the language selector panel. It shows the original language of the content. localeName is the name of the language that we have detected the content to be in.',
        'Original ({localeName})',
        { localeName },
      )
    }
    return availableLocales.find((locale) => locale.standard === currentLanguage.value)?.name ?? ''
  })

  const showLanguagePanel = (): void => {
    xSurfaceLanguagePanel.value = true
  }

  const closeLanguagePanel = (): void => {
    xSurfaceLanguagePanel.value = false
    // Reset unsaved language to current language
    unsavedLanguage.value = currentLanguage.value
  }

  async function translatePadletToLanguage(selectedLanguage: string): Promise<void> {
    try {
      isTranslatingPadlet.value = true
      currentLanguage.value = selectedLanguage
      const data = await WallTranslationApi.translate(surfaceStore.wallId, selectedLanguage)
      surfaceStore.updateWall({ title: data.title, description: data.description })
      surfaceSectionsStore.updateSectionEntitiesWithTranslatedTitles(data.sections)
      surfacePostsStore.updatePostsWithTranslations(data.wishes)
      surfaceCommentsStore.updateCommentsWithTranslatedBody(data.comments)
    } catch (error) {
      // TODO: ENG-19994 - Handle error state
    } finally {
      isTranslatingPadlet.value = false
      closeLanguagePanel()
    }
  }

  async function detectLanguage(): Promise<void> {
    try {
      const data = await WallLanguageDetectionApi.detectLanguage(surfaceStore.wallId)
      detectedLanguage.value = data.language
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceLanguageDetection' })
    }
  }

  const hasUnsavedLanguageChanges = computed(() => unsavedLanguage.value !== currentLanguage.value)

  const setUnsavedLanguage = (language: string): void => {
    unsavedLanguage.value = language
  }

  return {
    xSurfaceLanguagePanel,
    currentLanguage,
    detectedLanguage,
    unsavedLanguage,
    isTranslatingPadlet,
    showLanguagePanel,
    closeLanguagePanel,
    translatePadletToLanguage,
    detectLanguage,
    hasUnsavedLanguageChanges,
    setUnsavedLanguage,
    currentLanguageDisplayName,
  }
})
